/**
 * Notification Redux Slice
 * Manages notification state from WebSocket updates
 */

import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  items: [],
  unreadCount: 0,
  connected: false,
  lastUpdated: null,
};

const notificationSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    setNotifications: (state, action) => {
      state.items = action.payload;
      state.unreadCount = action.payload.filter((n) => !n.is_read).length;
      state.lastUpdated = new Date().toISOString();
    },

    addNotification: (state, action) => {
      // Add to beginning of list (newest first)
      state.items.unshift(action.payload);

      // Update unread count if notification is unread
      if (!action.payload.is_read) {
        state.unreadCount += 1;
      }

      state.lastUpdated = new Date().toISOString();
    },

    updateNotification: (state, action) => {
      const index = state.items.findIndex((n) => n.id === action.payload.id);

      if (index !== -1) {
        const wasUnread = !state.items[index].is_read;
        const isNowRead = action.payload.is_read;

        // Update notification
        state.items[index] = action.payload;

        // Adjust unread count if read status changed
        if (wasUnread && isNowRead) {
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }

        state.lastUpdated = new Date().toISOString();
      }
    },

    removeNotification: (state, action) => {
      const notification = state.items.find((n) => n.id === action.payload.id);

      // Decrease unread count if notification was unread
      if (notification && !notification.is_read) {
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }

      // Remove from list
      state.items = state.items.filter((n) => n.id !== action.payload.id);
      state.lastUpdated = new Date().toISOString();
    },

    setUnreadCount: (state, action) => {
      state.unreadCount = action.payload;
    },

    setConnected: (state, action) => {
      state.connected = action.payload;
    },

    // Track a response received for a notification the current user created
    updateNotificationResponse: (state, action) => {
      const { notificationId, response } = action.payload;
      const index = state.items.findIndex((n) => n.id === notificationId);
      if (index !== -1) {
        // Attach the response data to the notification for local display
        if (!state.items[index].received_responses) {
          state.items[index].received_responses = [];
        }
        state.items[index].received_responses.push(response);
      }
      state.lastUpdated = new Date().toISOString();
    },

    // Clear all notifications (e.g., on logout)
    clearNotifications: (state) => {
      state.items = [];
      state.unreadCount = 0;
      state.connected = false;
      state.lastUpdated = null;
    },
  },
});

export const {
  setNotifications,
  addNotification,
  updateNotification,
  removeNotification,
  setUnreadCount,
  setConnected,
  updateNotificationResponse,
  clearNotifications,
} = notificationSlice.actions;

// Selectors
export const selectNotifications = (state) => state.notifications.items;
export const selectUnreadCount = (state) => state.notifications.unreadCount;
export const selectNotificationConnected = (state) => state.notifications.connected;
export const selectLastUpdated = (state) => state.notifications.lastUpdated;

// Filtered selectors
export const selectUnreadNotifications = (state) =>
  state.notifications.items.filter((n) => !n.is_read);

export const selectNotificationsByType = (type) => (state) =>
  state.notifications.items.filter((n) => n.type === type);

export default notificationSlice.reducer;
